import { EyeOutlined, RetweetOutlined } from '@ant-design/icons';
import { AddPhotoIcon } from '@assets/icons/addPhoto';
import { Flex, Image as AntImage, message, Row, Typography } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { FC, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { isFileSizeLess } from 'src/features/programsV2/utils/validateFileSize';

import { MarketplaceRootData } from '../schemas/rootSchema';
import { TransparentButton } from './ui/TransparentButton';

interface MarketplaceImageUploadProps {
  title?: string;
  helperText?: string;
  fieldName: string;
  required?: boolean;
  maxDimensions: {
    width: number;
    height: number;
  };
}

const { Text } = Typography;

export const MarketplaceImageUpload: FC<MarketplaceImageUploadProps> = ({
  title,
  fieldName,
  helperText,
  required,
  maxDimensions,
}) => {
  const {
    setValue,
    formState: { errors },
    clearErrors,
    watch,
  } = useFormContext<MarketplaceRootData>();

  const imageData = watch(fieldName as any);
  const previewImage = imageData?.preview;

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handlePreview = (event: any) => {
    event.stopPropagation();
    setIsPreviewOpen(true);
  };

  const handleSetLocalPreviewImage = (
    imageUrl: string,
    file: RcFile | File
  ) => {
    setValue(
      fieldName as any,
      { preview: imageUrl, file },
      { shouldValidate: true }
    );
    clearErrors(fieldName as any);
  };

  const handleValidateFileType = (file: RcFile | File) => {
    const fileType = file.type;

    if (fileType !== 'image/png') {
      message.error(
        `${file.name.toLowerCase()} - não é um arquivo válido. Apenas arquivos PNG são permitidos.`
      );
      return false;
    }
    return true;
  };

  const validateImageDimensions = (file: RcFile | File): Promise<boolean> => {
    return new Promise(resolve => {
      const image = new Image();
      image.src = URL.createObjectURL(file);

      image.onload = () => {
        const { width, height } = image;
        URL.revokeObjectURL(image.src);

        if (width < maxDimensions?.width || height < maxDimensions?.height) {
          message.error(
            `A imagem deve ter ${maxDimensions?.width}x${maxDimensions?.height} pixels. ` +
              `Dimensões da imagem selecionada: ${width}x${height} pixels.`
          );
          resolve(false);
        }
        resolve(true);
      };
    });
  };

  const processImage = async (file: RcFile | File) => {
    if (!handleValidateFileType(file)) {
      return;
    }

    const isValidDimension = await validateImageDimensions(file);
    if (!isValidDimension) {
      return;
    }

    const isValidSize = isFileSizeLess(file as RcFile, 10);
    if (!isValidSize) {
      message.error('O arquivo deve ter no máximo 10MB');
      return;
    }

    const tempUrlImage = URL.createObjectURL(file);
    handleSetLocalPreviewImage(tempUrlImage, file);
  };

  const handleClick = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png';

    fileInput.addEventListener('change', async e => {
      const target = e.target as HTMLInputElement;
      if (!target.files || target.files.length === 0) {
        return;
      }
      const file = target.files[0];
      await processImage(file);
    });

    fileInput.click();
  };

  const handleChangeImage = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleClick();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length) {
      const file = droppedFiles[0] as RcFile;
      await processImage(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const error = get(errors, fieldName);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height: '100%',
      }}
    >
      <Row align='middle' style={{ marginBottom: '8px' }}>
        <Text strong>
          {title && title}
          {required && (
            <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
          )}
        </Text>
      </Row>
      <StyledUploadArea
        onClick={previewImage ? undefined : handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        isError={!!error}
        hasImage={!!previewImage}
      >
        {previewImage ? (
          <AntImage
            src={previewImage}
            alt='Preview'
            wrapperStyle={{
              width: '100%',
              height: '100%',
            }}
            width='auto'
            height='200px'
            style={{
              objectFit: 'cover',
            }}
            preview={{
              visible: isPreviewOpen,
              onVisibleChange: visible => setIsPreviewOpen(visible),
              mask: (
                <Flex gap={12}>
                  <TransparentButton
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                  />
                  <TransparentButton
                    icon={<RetweetOutlined />}
                    onClick={handleChangeImage}
                  />
                </Flex>
              ),
            }}
          />
        ) : (
          <DefaultPhotoContainer>
            <AddPhotoIcon />
            <p style={{ fontSize: 16 }}>
              <strong style={{ color: '#1668DC' }}>Clique para carregar</strong>{' '}
              ou arraste e solte
            </p>
            <Text type='secondary'>{helperText}</Text>
          </DefaultPhotoContainer>
        )}
      </StyledUploadArea>
      {!!error && (
        <Text type='danger' style={{ marginTop: '8px', display: 'block' }}>
          {error.message}
        </Text>
      )}
    </div>
  );
};

const StyledUploadArea = styled.div<{ isError: boolean; hasImage: boolean }>`
  border: ${({ isError }) =>
    isError ? '1px dashed red' : '2px dashed #424242'};
  border-radius: 8px;
  background: #141414;
  cursor: ${({ hasImage }) => (hasImage ? 'default' : 'pointer')};
  transition: border-color 0.3s;
  min-height: 176px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${({ isError, hasImage }) =>
      isError ? 'red' : hasImage ? '#d9d9d9' : '#1668DC'};
  }
`;

const DefaultPhotoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  flex: 1;
  padding: 16px;
`;
